import { textColorFields } from '../_shared/textColor.js';
import { mediaRadiusField, universalFields } from '../_shared/universal.js';
/**
 * colorways — LE NUANCIER. Une colonne centrée : un titre, les photos de la couleur choisie, son nom,
 * puis la rangée de pastilles. On clique une couleur, les photos changent. Référence apportée par la
 * fondatrice le 19/09 : la page HUAWEI Band 11 (« Every colour feels like your colour »).
 *
 * ── POURQUOI UNE SECTION À PART, ET PAS UN RÉGLAGE DE `featured-product` ───────────────────────────────
 *
 * `featured-product` sait déjà faire changer l'image au clic sur une variante : c'est la même mécanique
 * (`media-gallery` lit `imagesForVariant`, `variant-picker` écrit `variantId`). Mais c'est une FICHE
 * D'ACHAT — deux colonnes, prix, quantité, bouton d'achat, et un îlot React à hydrater sur la vitrine.
 * Le nuancier est une page de MARQUE : rien à acheter, rien à hydrater, une colonne centrée. Les deux
 * écarts qui manquaient là-bas sont ici la définition même de la section, pas des cases en plus :
 *
 *   1. la MISE EN PAGE empilée et centrée (`featured-product` n'a que média-à-gauche / média-à-droite) ;
 *   2. le CHOIX de la pastille — un rond de couleur MÊME quand la valeur a une photo. Dans
 *      `variant-picker` la photo gagne toujours (blocks/variant-picker/index.tsx), ce qui est juste
 *      pour une fiche produit et faux pour un nuancier de marque, où la référence montre des ronds.
 *
 * ── ZÉRO JAVASCRIPT ────────────────────────────────────────────────────────────────────────────────────
 *
 * Une radio masquée par couleur, toutes dans le même groupe, chacune POSÉE DANS l'étiquette qu'on clique
 * (voir Component.tsx : une radio groupée ailleurs fait sauter la page au clic — précédent 15/09 sur
 * `feature-gallery`). Aucun îlot, aucune entrée cliente, rien à hydrater : la section marche en SSR,
 * script coupé, et elle marche À L'IDENTIQUE dans le canvas du builder, qui n'arme aucun îlot.
 *
 * ── L'AXE D'IMAGES EST DÉRIVÉ, JAMAIS DÉCLARÉ ──────────────────────────────────────────────────────────
 *
 * Aucun réglage « quelle option est la couleur ? ». `imageAxisIndex()` trouve l'option dont les valeurs
 * étiquettent des photos — le vendeur l'a déjà dite en téléversant ses photos par couleur dans l'éditeur
 * de variantes. Un second champ à garder synchrone serait un champ de plus à casser sur un renommage.
 */
const CONTENT = 'Content';
const LAYOUT = 'Layout';
const SWATCHES = 'Swatches';
export const colorwaysSchema = {
    type: 'colorways',
    name: 'Colorways',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── Content ─────────────────────────────────────────────────────────────
        {
            id: 'product',
            type: 'product-picker',
            label: 'Product',
            group: CONTENT,
            info: 'The colors come from this product. Tag each photo with its color in the product’s variant editor — that is what makes the picture change when a color is clicked.',
        },
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: CONTENT,
            default: '',
            aiWritable: true,
            maxLength: 80,
        },
        // ── Layout ──────────────────────────────────────────────────────────────
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Image ratio',
            group: LAYOUT,
            default: 'auto',
            options: [
                { value: 'auto', label: 'Original (uncropped)' },
                { value: 'square', label: 'Square' },
                { value: 'portrait', label: 'Portrait' },
                { value: 'landscape', label: 'Landscape' },
            ],
        },
        {
            id: 'imagesPerColor',
            type: 'select',
            label: 'Photos per color',
            group: LAYOUT,
            default: 'all',
            options: [
                { value: 'one', label: 'One' },
                { value: 'all', label: 'All of them, stacked' },
            ],
            info: 'Every photo of every color is downloaded, whichever one is showing. With many colors and many photos, prefer “One”.',
        },
        // ── Swatches ────────────────────────────────────────────────────────────
        {
            id: 'swatchStyle',
            type: 'select',
            label: 'Swatch style',
            group: SWATCHES,
            default: 'color',
            options: [
                { value: 'color', label: 'Color dot' },
                { value: 'photo', label: 'Photo' },
                { value: 'auto', label: 'Photo when there is one, else a dot' },
            ],
            info: 'A value with no color and no photo always falls back to its name.',
        },
        {
            id: 'swatchSize',
            type: 'select',
            label: 'Swatch size',
            group: SWATCHES,
            default: 'medium',
            options: [
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
            ],
        },
        {
            id: 'showValueName',
            type: 'checkbox',
            label: 'Show the color name',
            group: SWATCHES,
            default: true,
            info: 'The name of the chosen color, between the photos and the swatches.',
        },
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
};
export const colorwaysPresets = [
    {
        name: 'Colorways',
        settings: {
            heading: '@t:section.colorways_heading',
            imageRatio: 'auto',
            imagesPerColor: 'all',
            swatchStyle: 'color',
            swatchSize: 'medium',
            showValueName: true,
            colorScheme: 'light',
            width: 'narrow',
            paddingTop: 72,
            paddingBottom: 72,
        },
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-three sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const colorwaysMeta = {
    label: 'Colorways',
    group: 'Product',
    icon: 'palette',
};
//# sourceMappingURL=schema.js.map